import { useQuery } from '@tanstack/react-query';

import type { OrderPaymentFai } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get order payment FAI (Finance, Aftermarket, Insurance) data by ID
 * @param servicesProviderId - Required services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const getOrderPaymentFai = async (
	servicesProviderId: string,
	orderId: string,
): Promise<OrderPaymentFai> => {
	const response = await apiClient.get<OrderPaymentFai>(
		ordersEndpoints.paymentFai(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for fetching order payment FAI (Finance, Aftermarket, Insurance) data by ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetOrderPaymentFai = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.paymentFai.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to fetch order payment FAI data without an order ID.');
			}
			return getOrderPaymentFai(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
